import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { cn } from "@/utils/cn";
import { ArrowDown, ArrowUp, ChevronDown, Filter, Loader2, Merge, Sparkles } from "lucide-react";
import { useState } from "react";
import { EMPTY, type Kind, type Sort, sortWords } from "../sheet";
import { FadeText } from "./Fade";

export type FieldMenu = {
  key: string;
  label: string;
  kind: Kind;
  values: { value: string; count: number }[];
  /** For a text column: rank its values by a criterion with our decision model. */
  smart?: { placeholder: string };
  /** For a text column: group its values under a few labels, in a dialog. */
  onNormalize?: () => void;
};

const item =
  "flex w-full items-center gap-2 whitespace-nowrap rounded-[2px] px-2 py-1 text-left text-xs normal-case tracking-normal text-zinc-700 hover:bg-zinc-100 disabled:opacity-40";

/** Sort and filter controls for a column (or the paper's fields), from its header. */
export const SheetMenu = ({
  fields,
  sort,
  filters,
  ranking,
  onSort,
  onSmart,
  onFilter,
  active,
}: {
  fields: FieldMenu[];
  sort: Sort | null;
  filters: Record<string, string[]>;
  /** The key being smart sorted. */
  ranking: string | null;
  onSort: (sort: Sort | null) => void;
  onSmart: (key: string, criterion: string) => void;
  onFilter: (key: string, values: string[] | null) => void;
  /** The header's sort or filter is on: the trigger shows it. */
  active: boolean;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Sort and filter"
          className={cn("shrink-0 pb-px hover:text-zinc-600", active ? "text-zinc-700" : "text-zinc-300")}
        >
          <ChevronDown className="h-3 w-3" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="max-h-[70vh] w-72 overflow-y-auto p-1.5 text-xs normal-case tracking-normal">
        {fields.map((field, i) => (
          <FieldControls
            key={field.key}
            field={field}
            titled={fields.length > 1}
            first={i === 0}
            sort={sort?.key === field.key ? sort : null}
            kept={filters[field.key] ?? null}
            ranking={ranking === field.key}
            onSort={(dir) => onSort(dir ? { key: field.key, dir } : null)}
            onSmart={(criterion) => onSmart(field.key, criterion)}
            onFilter={(values) => onFilter(field.key, values)}
          />
        ))}
      </PopoverContent>
    </Popover>
  );
};

const FieldControls = ({
  field,
  titled,
  first,
  sort,
  kept,
  ranking,
  onSort,
  onSmart,
  onFilter,
}: {
  field: FieldMenu;
  titled: boolean;
  first: boolean;
  sort: Sort | null;
  kept: string[] | null;
  ranking: boolean;
  onSort: (dir: "asc" | "desc" | null) => void;
  onSmart: (criterion: string) => void;
  onFilter: (values: string[] | null) => void;
}) => {
  const words = sortWords(field.kind);
  const [criterion, setCriterion] = useState(sort?.smart?.criterion ?? field.label);
  const [showValues, setShowValues] = useState(!titled || kept !== null);
  const on = (dir: "asc" | "desc") => sort?.dir === dir && !sort.smart;
  const toggle = (value: string) => {
    const all = field.values.map((v) => v.value);
    const current = kept ?? all;
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    onFilter(next.length === all.length ? null : next);
  };
  return (
    <div className={cn(!first && "mt-1 border-t border-zinc-100 pt-1")}>
      {titled && <p className="font-ui px-2 pb-0.5 pt-1 text-3xs uppercase tracking-wider text-zinc-400">{field.label}</p>}
      <div className="flex gap-1">
        <button type="button" className={cn(item, on("asc") && "bg-zinc-100 font-medium")} onClick={() => onSort(on("asc") ? null : "asc")}>
          <ArrowUp className="h-3 w-3" aria-hidden /> {words.asc}
        </button>
        <button type="button" className={cn(item, on("desc") && "bg-zinc-100 font-medium")} onClick={() => onSort(on("desc") ? null : "desc")}>
          <ArrowDown className="h-3 w-3" aria-hidden /> {words.desc}
        </button>
      </div>
      {field.smart && (
        <form
          className="mt-1 px-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (!ranking) onSmart(criterion.trim() || field.label);
          }}
        >
          <label className="flex items-center gap-1 px-1 text-2xs normal-case tracking-normal text-zinc-500">
            <Sparkles className="h-3 w-3" aria-hidden /> Smart sort by
          </label>
          <div className="mt-0.5 flex items-center gap-1">
            <input
              value={criterion}
              onChange={(e) => setCriterion(e.target.value)}
              placeholder={field.smart.placeholder}
              aria-label={`Smart sort ${field.label} by`}
              className="min-w-0 flex-1 rounded-[2px] border border-zinc-200 px-1.5 py-0.5 text-xs normal-case tracking-normal focus:border-zinc-400 focus:outline-none"
            />
            <button type="submit" disabled={ranking} className="rounded-[2px] bg-zinc-900 px-2 py-0.5 text-xs normal-case tracking-normal text-white disabled:opacity-50">
              {ranking ? <Loader2 className="h-3 w-3 animate-spin" aria-label="Ranking" /> : "Rank"}
            </button>
          </div>
          {sort?.smart && <p className="px-1 pt-0.5 text-2xs text-zinc-500">Ranked by “{sort.smart.criterion}”</p>}
        </form>
      )}
      {field.onNormalize && (
        <button type="button" className={cn(item, "mt-1")} onClick={field.onNormalize}>
          <Merge className="h-3 w-3" aria-hidden /> Normalize this column
        </button>
      )}
      {field.values.length > 0 && (
        <div className="mt-1">
          <button type="button" className={cn(item, "text-zinc-500")} onClick={() => setShowValues((v) => !v)}>
            <Filter className="h-3 w-3" aria-hidden /> Filter{kept ? ` · ${kept.length} of ${field.values.length}` : ""}
          </button>
          {showValues && (
            <div className="max-h-56 overflow-y-auto">
              <div className="flex gap-2 px-2 py-0.5 text-3xs text-zinc-500">
                <button type="button" className="hover:text-zinc-900" onClick={() => onFilter(null)}>All</button>
                <button type="button" className="hover:text-zinc-900" onClick={() => onFilter([])}>None</button>
              </div>
              {field.values.map(({ value, count }) => (
                <label key={value} className={cn(item, "cursor-pointer")}>
                  <input
                    type="checkbox"
                    checked={kept === null || kept.includes(value)}
                    onChange={() => toggle(value)}
                    className="h-3 w-3 rounded-[2px] border-zinc-300 text-zinc-900"
                  />
                  <FadeText className={cn("flex-1", value === EMPTY && "italic text-zinc-400")} title={value}>{value}</FadeText>
                  <span className="tabular-nums text-zinc-400">{count}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
